import { useEffect, useRef, useCallback, useState } from 'react';
import { logPlaybackError } from '../utils/logPlaybackError';

export interface PlaybackErrorState {
  hasError: boolean;
  message: string;
  /** True when the URL is reachable but the file isn't browser-playable (encoding may be needed). */
  isEncoding: boolean;
}

const NO_ERROR: PlaybackErrorState = { hasError: false, message: '', isEncoding: false };

/**
 * Read a debug flag from localStorage.
 * Keys are prefixed with "debug.player.*".
 */
function getDebugFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
}

/**
 * Hook that manages playback error handling:
 * - Codec detection via canPlayType()
 * - Error event listeners on media and source elements
 * - URL retry via /file/{fileId}/url
 * - Telemetry logging
 * - Debug overrides via localStorage
 */
export function usePlaybackErrorHandler(
  mediaEl: HTMLMediaElement | null,
  fileId: number | undefined,
  linkId?: string,
): { errorState: PlaybackErrorState; setError: (message: string, isEncoding?: boolean) => void; clearError: () => void; skipNextRetry: () => void } {
  const [errorState, setErrorState] = useState<PlaybackErrorState>(NO_ERROR);
  const hasRetriedRef = useRef(false);
  const isHandlingErrorRef = useRef(false);
  const skipRetryRef = useRef(false);

  const setError = useCallback((message: string, isEncoding = false) => {
    console.log("Failed to play back media file: " + message);
    setErrorState({ hasError: true, message, isEncoding });
  }, []);

  const clearError = useCallback(() => {
    setErrorState(NO_ERROR);
    hasRetriedRef.current = false;
    isHandlingErrorRef.current = false;
  }, []);

  /** Skip the automatic URL retry on the next error.  Used when the user
   *  explicitly switches sources — the retry would silently fall back to
   *  the proxy, making it look like nothing happened. */
  const skipNextRetry = useCallback(() => {
    skipRetryRef.current = true;
  }, []);

  useEffect(() => {
    if (!mediaEl) return;

    const failAll = getDebugFlag('debug.player.fail_all');
    const failCodec = failAll || getDebugFlag('debug.player.fail_codec_check');
    const failFirstUrl = failAll || getDebugFlag('debug.player.fail_first_url');
    const failRetryUrl = failAll || getDebugFlag('debug.player.fail_retry_url');

    // --- A. Codec detection & source selection ---
    const sourceEls = mediaEl.querySelectorAll('source');

    if (sourceEls.length > 0) {
      let foundCompatible = false;

      if (!failCodec) {
        for (let i = 0; i < sourceEls.length; i++) {
          const src = sourceEls[i];
          const mimeType = src.type || src.getAttribute('type') || '';

          if (mimeType && mediaEl.canPlayType(mimeType)) {
            // This source is compatible — set it as the active source
            mediaEl.src = src.src;
            mediaEl.load();
            foundCompatible = true;
            break;
          }
        }
      }

      if (!foundCompatible) {
        // No compatible sources found — check if we should try anyway
        // (some sources may lack type attributes)
        const hasTypedSources = Array.from(sourceEls).some(
          (s) => s.type || s.getAttribute('type'),
        );

        if (hasTypedSources || failCodec) {
          // Log telemetry for the first source
          if (fileId && sourceEls[0]?.src) {
            logPlaybackError(sourceEls[0].src, fileId, sourceEls[0].type);
          }

          // Check if a proxy is being encoded. If yes, show the encoding
          // progress overlay so the user sees queue position / percentage
          // and the page reloads when the proxy is ready. If no, show the
          // download overlay so the user can download the original file.
          if (fileId) {
            (async () => {
              try {
                const resp = await fetch(`/encoding/progress?id=${fileId}`);
                if (resp.ok) {
                  const data = await resp.json();
                  if (data.status === 'queued' || data.status === 'processing') {
                    setErrorState({
                      hasError: true,
                      message: 'Your browser cannot play this file.',
                      isEncoding: true,
                    });
                    return;
                  }
                }
              } catch {
                // Probe failed — fall through to download overlay
              }
              setErrorState({
                hasError: true,
                message: 'Your browser cannot play this file.',
                isEncoding: false,
              });
            })();
          } else {
            setErrorState({
              hasError: true,
              message: 'Your browser cannot play this file.',
              isEncoding: false,
            });
          }
          return; // No cleanup needed — no listeners attached
        }
        // If no sources have type attributes, let the browser try natively
      }
    }

    // --- B. Debug: simulate first URL failure ---
    if (failFirstUrl && !hasRetriedRef.current) {
      // Schedule a fake error to trigger the retry flow
      const timer = setTimeout(() => {
        handleMediaError();
      }, 100);
      return () => clearTimeout(timer);
    }

    // --- C. Error event handlers ---
    async function handleMediaError() {
      if (isHandlingErrorRef.current) return;
      isHandlingErrorRef.current = true;

      const currentSrc = mediaEl!.currentSrc || mediaEl!.src;

      // Log the error
      if (fileId && currentSrc) {
        let sourceMimeType = "Unknown";
        for (const src of sourceEls) {
          if (src.src === currentSrc) {
            sourceMimeType = src.type || src.getAttribute('type') || '';
            break;
          }
        }

        logPlaybackError(currentSrc, fileId, sourceMimeType);
      }

      // Attempt URL retry (skipped when the user explicitly switched sources)
      const shouldSkipRetry = skipRetryRef.current;
      skipRetryRef.current = false;
      if (fileId && !hasRetriedRef.current && !shouldSkipRetry) {
        hasRetriedRef.current = true;

        try {
          // Determine if the current source is a proxy or the original.
          // Proxy sources have data-proxy-id on their <source> element.
          const activeSrc = mediaEl!.currentSrc || mediaEl!.src;
          let retryEndpoint = `/file/${fileId}/url`;
          for (const srcEl of Array.from(sourceEls)) {
            if (srcEl.src === activeSrc && srcEl.dataset.proxyId) {
              retryEndpoint = `/proxy/${srcEl.dataset.proxyId}/url`;
              break;
            }
          }
          if (linkId) {
            retryEndpoint += `?link=${encodeURIComponent(linkId)}`;
          }

          const response = await fetch(retryEndpoint);
          const data = await response.json();

          if (data.url) {
            if (failRetryUrl || getDebugFlag('debug.player.fail_retry_url')) {
              setErrorState({
                hasError: true,
                message: 'This file failed to play.',
                isEncoding: false,
              });
              return;
            }

            // Retry with new URL
            mediaEl!.src = data.url;
            mediaEl!.load();
            isHandlingErrorRef.current = false;

            try {
              await mediaEl!.play();
            } catch {
              // play() may fail if user hasn't interacted — that's OK
            }
            return;
          }

          if (data.error) {
            console.error(`[KollabPlayer] URL retry failed: ${data.error}`);
          }
        } catch (fetchErr: any) {
          console.error(`[KollabPlayer] URL retry fetch failed: ${fetchErr.message}`);
        }
      }

      // All retries exhausted — probe the URL to determine if reachable
      const probeUrl = mediaEl!.currentSrc || mediaEl!.src || currentSrc;
      if (probeUrl) {
        try {
          const probeResp = await fetch(probeUrl, {
            method: 'GET',
            headers: { Range: 'bytes=0-0' },
          });
          if (probeResp.ok || probeResp.status === 206) {
            // URL is reachable but not browser-playable. Check if encoding
            // is in progress before showing the encoding overlay — otherwise
            // the user sees a "preparing for playback" message that polls
            // and reloads the page in an infinite loop.
            if (fileId) {
              try {
                const encResp = await fetch(`/encoding/progress?id=${fileId}`);
                if (encResp.ok) {
                  const encData = await encResp.json();
                  if (encData.status === 'queued' || encData.status === 'processing') {
                    setErrorState({
                      hasError: true,
                      message: 'This file cannot be played in your browser.',
                      isEncoding: true,
                    });
                    return;
                  }
                }
              } catch {
                // Encoding check failed — fall through to download overlay
              }
            }
            // URL reachable but not encoding — show download overlay
            setErrorState({
              hasError: true,
              message: 'This file cannot be played in your browser.',
              isEncoding: false,
            });
            return;
          }
        } catch {
          // Probe failed (network error, CORS, etc.) — fall through to generic error
        }
      }

      // URL unreachable or no URL — show generic download overlay
      setErrorState({
        hasError: true,
        message: 'This file failed to play.',
        isEncoding: false,
      });
    }

    function handleSourceError(e: Event) {
      const sourceEl = e.target as HTMLSourceElement;
      console.error(`[KollabPlayer] Source error: ${sourceEl.src}`);

      // Only trigger the full error handler if this is the last source
      const allSources = mediaEl!.querySelectorAll('source');
      const lastSource = allSources[allSources.length - 1];
      if (sourceEl === lastSource) {
        handleMediaError();
      }
    }

    // --- D. Detect partially-playable files (e.g. ProRes in MOV) ---
    // The browser may accept the container MIME type via canPlayType() but
    // fail to decode the video codec. Audio still plays, no error fires,
    // and videoWidth stays 0. Catch this at loadedmetadata time.
    // Only flag for video/* MIME types — audio-only files in a QuickTime
    // container legitimately have videoWidth === 0.
    function handleLoadedMetadata() {
      if (!(mediaEl instanceof HTMLVideoElement) || mediaEl.videoWidth > 0) return;

      // Determine the MIME type of the active source
      const activeSrc = mediaEl.currentSrc || mediaEl.src;
      let mimeType = '';
      for (const src of Array.from(sourceEls)) {
        if (src.src === activeSrc) {
          mimeType = (src.type || src.getAttribute('type') || '').split(';')[0].trim();
          break;
        }
      }

      if (mimeType.startsWith('video/')) {
        setErrorState({
          hasError: true,
          message: 'This file cannot be played in your browser.',
          isEncoding: true,
        });
        if (fileId) {
          logPlaybackError(activeSrc, fileId, mimeType);
        }
      }
    }

    // Attach error listeners
    mediaEl.addEventListener('error', handleMediaError);
    mediaEl.addEventListener('loadedmetadata', handleLoadedMetadata);

    const currentSourceEls = mediaEl.querySelectorAll('source');
    currentSourceEls.forEach((src) => {
      src.addEventListener('error', handleSourceError);
    });

    return () => {
      mediaEl.removeEventListener('error', handleMediaError);
      mediaEl.removeEventListener('loadedmetadata', handleLoadedMetadata);
      currentSourceEls.forEach((src) => {
        src.removeEventListener('error', handleSourceError);
      });
    };
  }, [mediaEl, fileId, linkId]);

  return { errorState, setError, clearError, skipNextRetry };
}
